import { useCallback } from 'react';

import { Boards } from '@air/api';
import { type Board, type Clip } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdateBoardInAllViews } from '~/utils/mutateUtils/GalleryBoards';

export type SetBoardThumbnailParams = {
  /** The id of the board you want to add the thumbnail on */
  boardId: Board['id'];
  /** The clip information for the desired thumbnail */
  clip: Pick<Clip, 'id' | 'assets'>;
};

export const useSetBoardThumbnail = () => {
  const { updateBoardInAllViews } = useUpdateBoardInAllViews();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const setBoardThumbnail = useCallback(
    async ({ boardId, clip }: SetBoardThumbnailParams) => {
      if (!workspaceId) {
        throw new Error('No workspaceId found');
      }

      try {
        const newBoard = {
          id: boardId,
          thumbnails: [clip.assets.image],
        };

        const response = await Promise.all([
          Boards.updateThumbnail({ boardId, workspaceId, clipId: clip.id }),
          updateBoardInAllViews(newBoard),
        ]);

        // The response supplies the parent and library needed to update the board's containing list.
        await updateBoardInAllViews(response[0]);

        return response[0];
      } catch (error) {
        throw reportErrorToBugsnag({
          error,
          context: 'Unable to update board thumbnail',
          metadata: { Data: { clip, boardId } },
        });
      }
    },
    [updateBoardInAllViews, workspaceId],
  );

  return {
    setBoardThumbnail,
  };
};
